import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, UserRound, Megaphone, Compass, Users, LogOut, Menu, X, PlusCircle, BadgeCheck, Wallet,
} from "lucide-react";

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const isPromoter = user?.role === "promoter";

  const nav = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    isPromoter
      ? { to: "/campaigns/new", label: "Post Campaign", icon: Megaphone }
      : { to: "/discover", label: "Discover Campaigns", icon: Compass },
    isPromoter
      ? { to: "/influencers", label: "Find Influencers", icon: Users }
      : { to: "/discover", label: "Find Work", icon: Compass, hide: true },
    { to: "/wallet", label: "Wallet", icon: Wallet },
    { to: "/profile", label: "Profile", icon: UserRound },
  ].filter((n) => !n.hide);

  const NavLinks = ({ onClick }) => (
    <>
      {nav.map((n) => {
        const active = location.pathname === n.to;
        const Icon = n.icon;
        return (
          <Link
            key={n.label}
            to={n.to}
            onClick={onClick}
            data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
              active
                ? "bg-neon-lime text-background"
                : "text-muted-foreground hover:bg-surfaceHover hover:text-foreground"
            }`}
          >
            <Icon className="h-4 w-4" />
            {n.label}
          </Link>
        );
      })}
    </>
  );

  return (
    <div className="min-h-screen bg-background flex">
      <title>InfluencerCollab</title>
      <meta name="robots" content="noindex,nofollow" />
      {/* Sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-border bg-surface p-5 fixed h-screen">
        <div className="mb-8"><Logo /></div>
        <nav className="flex flex-col gap-1.5 flex-1"><NavLinks /></nav>
        <div className="border-t border-border pt-4">
          <div className="flex items-center gap-3 px-2 mb-3">
            <img src={user?.picture} alt="" className="h-9 w-9 rounded-full object-cover bg-muted" />
            <div className="min-w-0">
              <div className="flex items-center gap-1">
                <p className="text-sm font-semibold truncate">{user?.name}</p>
                {user?.verified && <BadgeCheck className="h-3.5 w-3.5 text-neon-cyan" />}
              </div>
              <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="flex items-center gap-2 px-2 py-2 text-sm text-muted-foreground hover:text-destructive transition-colors w-full"
          >
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 flex items-center justify-between px-4 h-16 border-b border-border bg-background/80 backdrop-blur-2xl">
        <Logo size="sm" />
        <button onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" className="p-2">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden fixed inset-0 z-30 bg-background/95 backdrop-blur-2xl pt-20 px-5">
          <nav className="flex flex-col gap-2"><NavLinks onClick={() => setOpen(false)} /></nav>
          <button onClick={logout} className="flex items-center gap-2 px-4 py-3 mt-4 text-destructive">
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 lg:ml-64 pt-16 lg:pt-0 min-w-0">
        {isPromoter && (
          <div className="hidden lg:flex justify-end px-8 pt-6">
            <Button
              onClick={() => navigate("/campaigns/new")}
              data-testid="quick-post-campaign-btn"
              className="rounded-full bg-neon-pink text-white hover:bg-neon-pink/90 font-semibold"
            >
              <PlusCircle className="h-4 w-4 mr-1" /> New Campaign
            </Button>
          </div>
        )}
        <div className="p-5 sm:p-8 relative z-10">{children}</div>
      </main>
    </div>
  );
}
